/**
 * Conduz o piloto de ponta a ponta em LOTE: coleta até 50 e-mails novos da
 * caixa real e faz o resto do clique só com eles — tria o lote, lê as origens
 * de thread do lote uma a uma e, por fim, promove a Caso o que a leitura deixou
 * pronto. O clique seguinte continua a caixa de onde este parou.
 *
 * Uma chamada por passo, e este hook decide quando parar — a API não roda nada
 * em segundo plano. Os tickets aparecem na fila de triagem, e os que o portão
 * deixar passar entram na captação pelo `GET /captacao` de sempre.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ErroDaApi } from '../../../shared/api/erros';
import { coletarCaixa, lerProximaOrigem, promoverTickets, triarCaixa } from './api/demoDoPipeline';

export type EtapaDaDemo = 'parada' | 'coletando' | 'triando' | 'lendo' | 'promovendo' | 'pronta' | 'falhou';

/** E-mails novos por clique — o mesmo `LoteDaColeta` da API. */
export const LOTE_DA_DEMO = 50;

export interface EstadoDaDemo {
  readonly etapa: EtapaDaDemo;
  readonly coletadas: number | null;
  /** A coleta parou no lote: ainda há e-mail novo na caixa para o próximo clique. */
  readonly sobrouNaCaixa: boolean;
  readonly triados: number | null;
  readonly lidos: number;
  readonly recebimentos: number;
  readonly falhasDeLeitura: number;
  /** Em quantas leituras o agente rodou. */
  readonly comAgente: number;
  /** Quantos tickets viraram Caso na promoção que fecha a rodada (`null` até ela rodar). */
  readonly promovidos: number | null;
  /** Por que parou antes de ler o lote inteiro, quando foi o caso. */
  readonly motivo: string | null;
  readonly erro: ErroDaApi | null;
}

export interface OpcoesDaDemo {
  /**
   * Teto de leituras numa rodada: o custo de um clique. Com o agente em
   * `sempre` (`Parser__AgentMode`), cada leitura chama o agente (pago). O lote
   * já limita o clique a 50 e-mails; o teto é a segunda trava.
   */
  readonly teto?: number;
  /** Chamado a cada passo concluído — para quem mostra a fila recarregá-la. */
  readonly aoAvancar?: () => void;
}

const INICIAL: EstadoDaDemo = {
  etapa: 'parada',
  coletadas: null,
  sobrouNaCaixa: false,
  triados: null,
  lidos: 0,
  recebimentos: 0,
  falhasDeLeitura: 0,
  comAgente: 0,
  promovidos: null,
  motivo: null,
  erro: null,
};

export function useDemoDoPipeline({ teto = LOTE_DA_DEMO, aoAvancar }: OpcoesDaDemo = {}) {
  const [estado, setEstado] = useState<EstadoDaDemo>(INICIAL);
  const controle = useRef<AbortController | null>(null);

  useEffect(() => () => controle.current?.abort(), []);

  const iniciar = useCallback(async () => {
    controle.current?.abort();
    const atual = new AbortController();
    controle.current = atual;
    const sinal = atual.signal;

    try {
      setEstado({ ...INICIAL, etapa: 'coletando' });
      const coleta = await coletarCaixa(sinal);
      const lote = coleta.emailIds;
      setEstado((e) => ({ ...e, coletadas: coleta.coletadas, sobrouNaCaixa: coleta.sobrouNaCaixa }));
      aoAvancar?.();

      // Nada novo na caixa: não há lote para triar nem ler.
      if (lote.length === 0) {
        setEstado((e) => ({ ...e, etapa: 'pronta', motivo: 'Nenhum e-mail novo na caixa.' }));
        return;
      }

      setEstado((e) => ({ ...e, etapa: 'triando' }));
      const triagem = await triarCaixa(lote, sinal);
      setEstado((e) => ({ ...e, triados: triagem.abertos + triagem.vinculados, etapa: 'lendo' }));
      aoAvancar?.();

      // Fecha a rodada: a promoção é a mesma do ciclo de leitura, só que disparada pelo clique.
      const concluir = async (motivo: string | null) => {
        setEstado((e) => ({ ...e, etapa: 'promovendo' }));
        const promocao = await promoverTickets(sinal);
        setEstado((e) => ({ ...e, etapa: 'pronta', promovidos: promocao.promovidos, motivo }));
        aoAvancar?.();
      };

      let cursor: string | null = null;
      for (let lidos = 1; lidos <= teto; lidos++) {
        const leitura = await lerProximaOrigem(cursor, lote, sinal);
        if (leitura.acabou) {
          await concluir(null);
          return;
        }
        cursor = leitura.recebidoEm;
        aoAvancar?.();
        const recebimento = leitura.classe === 'Receipt';
        const falhou = leitura.falha !== null;
        setEstado((e) => ({
          ...e,
          lidos,
          recebimentos: e.recebimentos + (recebimento ? 1 : 0),
          falhasDeLeitura: e.falhasDeLeitura + (falhou ? 1 : 0),
          comAgente: e.comAgente + (leitura.agenteRodou ? 1 : 0),
        }));
      }
      await concluir(`Parou no teto de ${String(teto)} leituras.`);
    } catch (causa) {
      if (causa instanceof ErroDaApi && causa.origem === 'cancelado') return;
      setEstado((e) => ({
        ...e,
        etapa: 'falhou',
        erro: causa instanceof ErroDaApi ? causa : null,
        motivo: causa instanceof ErroDaApi ? causa.message : 'Falha inesperada.',
      }));
    }
  }, [teto, aoAvancar]);

  return { estado, iniciar, lote: LOTE_DA_DEMO };
}
